import { useEffect } from "react"
import noop from "lib/noop"
import { Box, IconButton, Stack, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { If } from "lib/@components/switch"
import { getMaintenanceTask } from "routes/component-library/controller/maintenance-library"
import { parseComponentTokenId } from "authoring/routes/macro/lib/parse-component-token-id"
import { withComparison } from "lib/graphql/comparison-function"
import { isSame } from "library/normalized-stringify/index.mjs"
import { Classification } from "routes/schedule/tasks/classification"
import uniqolor from "uniqolor"

export function ComponentInUseDetail({ tokenId, onBack, scheduleTasks = [] }) {
    const parsed = parseComponentTokenId(tokenId)
    const task = getMaintenanceTask.useResults(
        parsed?.taskId,
        withComparison((left, right) => isSame(left, right, { invalidChars: [] }))
    )

    useEffect(scrollToMatchingTask, [parsed?.taskId, scheduleTasks])

    if (!task) return null

    const instructions = task.componentInstructionsHtml
    const minutes = task.timing?.minutes
    const rounded = minutes != null ? Math.round(minutes) : null
    const freq = task.frequency
    const freqLabel = freq ? `${freq.interval}${freq.period?.[0] || "U"}` : task.frequencyCode

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton aria-label="Back" size="small" onClick={onBack}>
                    <Iconify icon="mdi:chevron-left" />
                </IconButton>
                <If value={task.classification}>
                    <Classification classification={task.classification} />
                </If>
                <Typography variant="subtitle2" noWrap sx={{ minWidth: 0 }}>
                    {task.heading || "Untitled Task"}
                </Typography>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ pl: 4.5 }}>
                <If value={rounded != null}>
                    <Typography variant="body2">{rounded}m</Typography>
                </If>
                <If value={freqLabel}>
                    <Typography variant="body2">
                        <strong>Every </strong>
                        {freqLabel}
                    </Typography>
                </If>
                <If value={task.skillSet}>
                    <Box
                        sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: `${uniqolor(task.skillSet).color}40`,
                            textAlign: "center",
                            minWidth: 32,
                        }}
                    >
                        <Typography variant="body2">{task.skillSet}</Typography>
                    </Box>
                </If>
            </Stack>

            <If value={instructions}>
                <Stack spacing={0.75} sx={{ pl: 0.5 }}>
                    <Typography variant="subtitle2">Component Instructions</Typography>
                    <Box
                        className="styled-bullets"
                        sx={{
                            typography: "body2",
                            wordBreak: "break-word",
                            "& p": { mt: 0, mb: 0.5 },
                            "& p:last-of-type": { mb: 0 },
                        }}
                        dangerouslySetInnerHTML={{ __html: instructions }}
                    />
                </Stack>
            </If>
        </Stack>
    )

    function scrollToMatchingTask() {
        if (!parsed?.taskId || !scheduleTasks?.length) return noop

        const scheduleTask = scheduleTasks.find(matchesComponentRef)
        if (!scheduleTask?.id) return noop

        const element = document.querySelector(`[data-cy="task-row-${scheduleTask.id}"]`)
        if (!element) return noop

        element.scrollIntoView({ behavior: "smooth", block: "center" })

        const original = element.style.transition
        element.style.transition = "box-shadow 0.3s ease, background-color 0.3s ease"
        element.style.boxShadow = "inset 0 0 0 2px rgba(24, 144, 255, 0.4)"
        element.style.backgroundColor = "rgba(24, 144, 255, 0.06)"

        const timer = setTimeout(clearHighlight, 2000)
        return () => {
            clearTimeout(timer)
            clearHighlight()
        }

        function clearHighlight() {
            element.style.boxShadow = ""
            element.style.backgroundColor = ""
            setTimeout(() => {
                element.style.transition = original
            }, 300)
        }

        function matchesComponentRef(t) {
            return t.componentRef === parsed.taskId
        }
    }
}
